export interface TimeBlock {
  from: string;
  to: string;
}

export interface MemberAvailability {
  monday: { enabled: boolean; timeBlocks: TimeBlock[] };
  tuesday: { enabled: boolean; timeBlocks: TimeBlock[] };
  wednesday: { enabled: boolean; timeBlocks: TimeBlock[] };
  thursday: { enabled: boolean; timeBlocks: TimeBlock[] };
  friday: { enabled: boolean; timeBlocks: TimeBlock[] };
  saturday: { enabled: boolean; timeBlocks: TimeBlock[] };
  sunday: { enabled: boolean; timeBlocks: TimeBlock[] };
}

export interface BookingNotificationSettings {
  enabled: boolean;
  // Map of service name to scope. If a service is not in the map, it's disabled
  services: Record<string, "only-mine" | "all-bookings">;
}

export interface MemberNotifications {
  taskAssigned: boolean;
  taskUpdated: boolean;
  labResults: boolean;
  bookingMade: BookingNotificationSettings;
  bookingRescheduledOrCancelled: BookingNotificationSettings;
}

export interface Member {
  id: string;
  title: string;
  forename: string;
  surname: string;
  email: string;
  role: string;
  isClinician: boolean;
  active: boolean;
  startDate?: string;
  endDate?: string;
  qualifications?: string[];
  registrations?: string[];
  servicesProvided?: string[];
  pharmacies?: string[];
  notifications?: MemberNotifications;
  availability?: MemberAvailability;
}

export interface PendingInvite {
  id: string;
  email: string;
  role: string;
  selectedTeams: string[];
  message?: string;
  invitedBy: string;
  invitedAt: string;
  lastSentAt: string;
  startDate?: string;
  endDate?: string;
}

// Available options for member forms
export const availableRoles = [
  "Admin",
  "Pharmacist",
  "Manager",
  "Assistant",
  "Trainee Pharmacist",
  "Technician",
];

export const availableQualifications = [
  "MPharm (Master of Pharmacy)",
  "BPharm (Bachelor of Pharmacy)",
  "PharmD (Doctor of Pharmacy)",
  "MSc Clinical Pharmacy",
  "PGDip Pharmacy Practice",
  "Independent Prescriber",
  "Supplementary Prescriber",
];

export const availableRegistrations = [
  "GPhC Registration",
  "PSNI Registration",
  "Independent Prescriber Registration",
  "Supplementary Prescriber Registration",
  "Controlled Drug Registration",
  "Advanced Practice Registration",
];

export const availableServices = [
  "Ear Wax Removal",
  "Blood Pressure Check",
  "Vaccination Administration",
  "Medicine Use Review",
  "New Medicine Service",
  "Travel Health Consultation",
  "Emergency Contraception",
  "Smoking Cessation",
];

export const availablePharmacies = [
  "TITAN Pharmacy",
  "City Centre Pharmacy",
  "Westbury Pharmacy",
  "Clifton Pharmacy",
  "Redfield Pharmacy",
];

export const dayNames = [
  "monday",
  "tuesday",
  "wednesday",
  "thursday",
  "friday",
  "saturday",
  "sunday",
] as const;

export const dayLabels = [
  "Monday",
  "Tuesday",
  "Wednesday",
  "Thursday",
  "Friday",
  "Saturday",
  "Sunday",
];

// Default member template for forms
export const defaultMemberTemplate = {
  title: "Mr.",
  forename: "",
  surname: "",
  email: "",
  isClinician: false,
  startDate: "",
  endDate: "",
  qualifications: [],
  registrations: [],
  role: "",
  servicesProvided: [],
  pharmacies: [],
  notifications: {
    taskAssigned: true,
    taskUpdated: true,
    labResults: false,
    bookingMade: {
      enabled: true,
      services: {}, // Empty object means no services selected (all disabled)
    },
    bookingRescheduledOrCancelled: {
      enabled: true,
      services: {}, // Empty object means no services selected (all disabled)
    },
  },
  availability: {
    monday: { enabled: true, timeBlocks: [{ from: "09:00", to: "17:00" }] },
    tuesday: { enabled: true, timeBlocks: [{ from: "09:00", to: "17:00" }] },
    wednesday: { enabled: true, timeBlocks: [{ from: "09:00", to: "17:00" }] },
    thursday: { enabled: true, timeBlocks: [{ from: "09:00", to: "17:00" }] },
    friday: { enabled: true, timeBlocks: [{ from: "09:00", to: "17:00" }] },
    saturday: { enabled: false, timeBlocks: [] },
    sunday: { enabled: false, timeBlocks: [] },
  },
};

export const initialMembers: Member[] = [
  {
    id: "1",
    title: "Ms.",
    forename: "Ayesha",
    surname: "Khan",
    email: "ayesha@titanverse.health",
    role: "Pharmacist",
    isClinician: true,
    active: true,
    availability: {
      monday: { enabled: true, timeBlocks: [{ from: "08:00", to: "17:00" }] },
      tuesday: { enabled: true, timeBlocks: [{ from: "08:00", to: "17:00" }] },
      wednesday: {
        enabled: true,
        timeBlocks: [{ from: "08:00", to: "17:00" }],
      },
      thursday: { enabled: true, timeBlocks: [{ from: "08:00", to: "17:00" }] },
      friday: { enabled: true, timeBlocks: [{ from: "08:00", to: "17:00" }] },
      saturday: { enabled: false, timeBlocks: [] },
      sunday: { enabled: false, timeBlocks: [] },
    },
  },
  {
    id: "2",
    title: "Mr.",
    forename: "Bilal",
    surname: "Ahmed",
    email: "bilal@titanverse.health",
    role: "Pharmacist",
    isClinician: true,
    active: true,
    startDate: "2025-03-01",
    endDate: "2025-09-30",
    availability: {
      monday: { enabled: true, timeBlocks: [{ from: "09:00", to: "18:00" }] },
      tuesday: { enabled: true, timeBlocks: [{ from: "09:00", to: "18:00" }] },
      wednesday: { enabled: false, timeBlocks: [] },
      thursday: { enabled: true, timeBlocks: [{ from: "09:00", to: "18:00" }] },
      friday: { enabled: true, timeBlocks: [{ from: "09:00", to: "18:00" }] },
      saturday: { enabled: true, timeBlocks: [{ from: "10:00", to: "14:00" }] },
      sunday: { enabled: false, timeBlocks: [] },
    },
  },
  {
    id: "3",
    title: "Ms.",
    forename: "Fatima",
    surname: "Malik",
    email: "fatima@titanverse.health",
    role: "Pharmacist",
    isClinician: true,
    active: true,
    availability: {
      monday: { enabled: true, timeBlocks: [{ from: "10:00", to: "19:00" }] },
      tuesday: { enabled: true, timeBlocks: [{ from: "10:00", to: "19:00" }] },
      wednesday: {
        enabled: true,
        timeBlocks: [{ from: "10:00", to: "19:00" }],
      },
      thursday: { enabled: true, timeBlocks: [{ from: "10:00", to: "19:00" }] },
      friday: { enabled: true, timeBlocks: [{ from: "10:00", to: "19:00" }] },
      saturday: { enabled: false, timeBlocks: [] },
      sunday: { enabled: false, timeBlocks: [] },
    },
  },
  {
    id: "4",
    title: "Mr.",
    forename: "Hassan",
    surname: "Ali",
    email: "hassan@titanverse.health",
    role: "Pharmacist",
    isClinician: true,
    active: true,
    availability: {
      monday: { enabled: true, timeBlocks: [{ from: "08:00", to: "16:00" }] },
      tuesday: { enabled: true, timeBlocks: [{ from: "08:00", to: "16:00" }] },
      wednesday: {
        enabled: true,
        timeBlocks: [{ from: "08:00", to: "16:00" }],
      },
      thursday: { enabled: true, timeBlocks: [{ from: "08:00", to: "16:00" }] },
      friday: { enabled: true, timeBlocks: [{ from: "08:00", to: "16:00" }] },
      saturday: { enabled: false, timeBlocks: [] },
      sunday: { enabled: false, timeBlocks: [] },
    },
  },
  {
    id: "5",
    title: "Ms.",
    forename: "Zainab",
    surname: "Hussain",
    email: "zainab@titanverse.health",
    role: "Pharmacist",
    isClinician: true,
    active: true,
    endDate: "2026-06-30",
    availability: {
      monday: { enabled: false, timeBlocks: [] },
      tuesday: { enabled: false, timeBlocks: [] },
      wednesday: {
        enabled: true,
        timeBlocks: [{ from: "08:00", to: "20:00" }],
      },
      thursday: { enabled: true, timeBlocks: [{ from: "08:00", to: "20:00" }] },
      friday: { enabled: true, timeBlocks: [{ from: "08:00", to: "20:00" }] },
      saturday: { enabled: true, timeBlocks: [{ from: "09:00", to: "17:00" }] },
      sunday: { enabled: true, timeBlocks: [{ from: "10:00", to: "16:00" }] },
    },
  },
  {
    id: "6",
    title: "Mr.",
    forename: "Imran",
    surname: "Sheikh",
    email: "imran@titanverse.health",
    role: "Pharmacist",
    isClinician: true,
    active: true,
    availability: {
      monday: { enabled: true, timeBlocks: [{ from: "12:00", to: "20:00" }] },
      tuesday: { enabled: true, timeBlocks: [{ from: "12:00", to: "20:00" }] },
      wednesday: {
        enabled: true,
        timeBlocks: [{ from: "12:00", to: "20:00" }],
      },
      thursday: { enabled: true, timeBlocks: [{ from: "12:00", to: "20:00" }] },
      friday: { enabled: false, timeBlocks: [] },
      saturday: { enabled: true, timeBlocks: [{ from: "09:00", to: "17:00" }] },
      sunday: { enabled: true, timeBlocks: [{ from: "10:00", to: "18:00" }] },
    },
  },
  {
    id: "7",
    title: "Mr.",
    forename: "Tas",
    surname: "Bhatti",
    email: "tas@titanverse.health",
    role: "Manager",
    isClinician: false,
    active: true,
  },
  {
    id: "8",
    title: "Ms.",
    forename: "Sophie",
    surname: "Taylor",
    email: "sophie.taylor@titanverse.health",
    role: "Technician",
    isClinician: false,
    active: true,
    startDate: "2025-06-01",
    endDate: "2026-12-31",
  },
  {
    id: "9",
    title: "Mrs.",
    forename: "Rachel",
    surname: "Martinez",
    email: "rachel.martinez@titanverse.health",
    role: "Assistant",
    isClinician: false,
    active: true,
  },
];

export const initialPendingInvites: PendingInvite[] = [
  {
    id: "inv-1",
    email: "john.smith@example.com",
    role: "Pharmacist",
    selectedTeams: [],
    message: "Welcome to the team!",
    invitedBy: "Ayesha Khan",
    invitedAt: "2024-01-15T10:30:00Z",
    lastSentAt: "2024-01-15T10:30:00Z",
    endDate: "2026-03-31",
  },
  {
    id: "inv-2",
    email: "sarah.wilson@example.com",
    role: "Technician",
    selectedTeams: [],
    invitedBy: "Tas Bhatti",
    invitedAt: "2024-01-18T14:45:00Z",
    lastSentAt: "2024-01-18T14:45:00Z",
    endDate: "2026-08-15",
  },
];
